<template>
  <div class="curation-parts-editor">
    <template v-if="editstate.mode === 'full'">
      <div v-if="def.parts && def.parts.length == 0" class="part-first-line">
        <button
          class="add-button jps-icon-button jps-sub-orange"
          @click="selectPart(0)"
        >
          <BaseIcon
            :alt="ls({ ja: 'パーツを追加', en: 'Add Component' })"
            name="mdi/plus-circle"
            size="size-2"
          />
        </button>
      </div>
      <div v-else class="part-split-line">
        <button
          class="add-button jps-icon-button jps-sub-orange"
          @click="selectPart(0)"
        >
          <BaseIcon
            :alt="ls({ ja: 'パーツを追加', en: 'Add Component' })"
            name="mdi/plus-circle"
            size="size-2"
          />
        </button>
      </div>
    </template>
    <div v-for="(part, index) in def.parts" :key="part.cpid" :id="part.cpid">
      <Component
        :is="editorComponent(part.type + '-editor')"
        :def="part"
        :editstate="editstate"
        @delete="deletePart"
      ></Component>
      <div class="part-split-line" v-if="editstate.mode === 'full'">
        <button
          class="add-button jps-icon-button jps-sub-orange"
          @click="selectPart(index + 1)"
        >
          <BaseIcon
            :alt="ls({ ja: 'パーツを追加', en: 'Add Component' })"
            name="mdi/plus-circle"
            size="size-2"
          />
        </button>
      </div>
    </div>
    <PartSelectModal ref="partSelectModal" :editstate="editstate" />
  </div>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { PartSelectModal } from '#components'

const { ls } = useI18n()
const { snackbar } = useSnackbar()

interface Props {
  editstate: CurationEditState,
  def: {
    parts: CurationPartDefinition[]
  }
}
const props = withDefaults(defineProps<Props>(), {})

const editorComponentMap = {
  'jps-curation-text-editor': defineAsyncComponent(() => import('../parts/text/CurationTextEditor.vue')),
  'jps-curation-image-editor': defineAsyncComponent(() => import('../parts/image/CurationImageEditor.vue')),
  'jps-curation-list-editor': defineAsyncComponent(() => import('../parts/list/CurationListEditor.vue')),
  'jps-curation-search-box-editor': defineAsyncComponent(() => import('../parts/search-box/CurationSearchBoxEditor.vue')),
  'jps-curation-image-annotation-editor': defineAsyncComponent(() => import('../parts/image-annotation/CurationImageAnnotationEditor.vue')),
  'jps-curation-timeline-editor': defineAsyncComponent(() => import('../parts/timeline/CurationTimelineEditor.vue')),
  'jps-curation-section-editor': defineAsyncComponent(() => import('../parts/section/CurationSectionEditor.vue')),
  'jps-curation-index-editor': defineAsyncComponent(() => import('../parts/index/CurationIndexEditor.vue')),
  'jps-curation-subpage-list-editor': defineAsyncComponent(() => import('../parts/subpage-list/CurationSubpageListEditor.vue')),
  'jps-curation-iframe-editor': defineAsyncComponent(() => import('../parts/iframe/CurationIframeEditor.vue')),
  'jps-curation-tab-editor': defineAsyncComponent(() => import('../parts/tab/CurationTabEditor.vue')),
}
const editorComponent = (type: string) => {
  return editorComponentMap[type] ?
    editorComponentMap[type]
    : null
}

const partSelectModal = ref<InstanceType<typeof PartSelectModal>>()
const smoothScroll = inject('smoothScroll') as (payload?: any) => void
const selectPart = async (i: number) => {
  try {
    const parts = await partSelectModal.value.openAwait()
    if (parts && parts.length) {
      props.def.parts.splice(i, 0, ...parts)
      // 追加したパーツが画面の先頭に来るようにスクロールを実施する処理
      // 旧JPSにも処理がはいってはいた(上手く動いてなかった様子)のでコメントアウトで残しておく
      // nextTick(() => {
      //   let payload = {
      //     scrollTo: document.getElementById(parts[0].cpid),
      //     duration: 500,
      //     container: undefined
      //   }
      //   const container = document.getElementById('jps-curation-editor-main')
      //   if (container) {
      //     payload.container = container
      //   }
      //   smoothScroll(payload)
      // })
    }
  } catch (cancel) {}
}

const deletePart = (part: CurationPartDefinition) => {
  removeFromArray(props.def.parts, part)
  if (props.editstate.snapshot) {
    props.editstate.snapshot()
  }
  snackbar(
    {
      message: dapLangGlobal.deleted,
      actionText: dapLangGlobal.undo,
      action: () => {
        props.editstate.undo()
      },
    }
  )
}
</script>

<style lang="scss">
@use '@/assets/styles/scss/vue' as *;

.editor-mode-full {
  .curation-editor-header {
    .representative-image {
      background: $jps-gray-100;
    }
  }
  .curation-parts-editor {
    @include is-mobile() {
      padding-left: 0;
      padding-right: 0;
    }

    .part-editor {
      border: 1px solid $jps-gray-300;
      border-radius: $global-radius-large;
      background: $jps-gray-100;
      @include panel-shadow();
      margin: 1rem;
      @include is-mobile {
        .part-editor {
          margin: 0.5rem;
        }
      }
    }
    .editor-tools {
      position: relative;
      display: flex;
      justify-content: flex-end;
      width: 100%;
      align-items: center;
      padding-top: 0.5rem;
      padding-bottom: 2px;
      margin-bottom: 0.5rem;
      @include is-mobile() {
        padding-top: 1rem;
      }
      .type-label {
        position: absolute;
        margin-right: auto;
        width: fit-content;
        font-size: 0.75rem;
        /* color: $jps-sub-orange; */
        padding-left: 6px;
        padding-right: 6px;
        top: -10px;
        border: 1px solid $jps-gray-300;
        border-radius: 3px;
        font-weight: bold;
        left: 16px;
        background: $white;
      }
      .part-menu {
        .jps-icon {
          font-size: 1.25rem;
        }
        padding-right: 6px;
      }
    }
    .editor-body {
      padding: 1rem;
      @include is-mobile {
        padding: 0.5rem;
      }
    }
  }
}
.curation-section-editor .curation-parts-editor .part-editor {
  background: $jps-gray-50;
}
.editor-mode-note {
  .editor-tools {
    display: none !important;
  }
}
</style>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.curation-parts-editor {
  .part-first-line,
  .part-split-line {
    padding: 0.5rem;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .add-button {
    height: 1.5rem;
    display: flex;
    align-items: center;
    @include is-mobile() {
      height: 2rem;
      &.jps-icon {
        font-size: 2rem;
      }
      .jps-icon {
        font-size: 2rem;
      }
    }
  }
}
</style>
